import { Component, OnInit, Inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { LoaderService } from 'src/app/services/loader.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { OpgaveEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/opgave-enum';
import { CreateServiceordreRequestDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/create-serviceordre-request-dto';
import { ServiceordreService } from 'src/app/api/services';
import { finalize, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { CreateServiceOrdreComponentDialogData } from 'src/app/components/aftale/models';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ResendServiceordredRequestDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/resend-serviceordred-request-dto';
import { NotificationType } from 'src/app/notifications/models/notification-models';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { ConfirmCreateDialogService } from '../../confirm-create-dialog/confirm-create-dialog.service';
import { KreditvaerdigEnumDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/kreditvaerdig-enum-dto';
import { KreditvurderingDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/kreditvurdering-dto';

@Component({
    selector: 'app-create-eftersyn',
    templateUrl: './create-eftersyn.component.html',
    styleUrls: ['./create-eftersyn.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateEftersynComponent implements OnInit {
  kreditvaerdigEnum = KreditvaerdigEnumDto;
  form: UntypedFormGroup;
  isLoading = false;
  validationErrors: any[] = [];
  today: Date = new Date();
  serviceaftale: ServiceaftaleDto;
  serviceordre: ServiceordreDto;
  kreditvurdering: KreditvurderingDto;
  partnerEndateSoon: boolean;

  unsubscribe$ = new Subject<void>();

  get note() {
    return this.form.get('note');
  }

  get deadline() {
    return this.form.get('deadline');
  }

  get fastOrdreNote() {
    return this.form.get('fastOrdreNote');
  }

  get fakturaNote() {
    return this.form.get('fakturaNote');
  }

  get accept() {
    return this.form.get('accept');
  }

  get acceptTekst() {
    return this.form.get('acceptTekst');
  }

  get minDeadlineDate() {
    if (this.serviceaftale.startDate != null) {
      const startDate = new Date(this.serviceaftale.startDate);
      if (startDate > this.today) {
        return startDate;
      } else {
        return this.today;
      }
    }
    return this.today;
  }

  constructor(
    private loader: LoaderService,
    private formBuilder: UntypedFormBuilder,
    private serviceordreService: ServiceordreService,
    private errorHandler: ErrorHandlerService,
    private notification: NotificationService,
    private dialogRef: MatDialogRef<CreateEftersynComponent>,
    private confirmCreateDialogService: ConfirmCreateDialogService,
    @Inject(MAT_DIALOG_DATA) public data: CreateServiceOrdreComponentDialogData
  ) {
    this.serviceaftale = data.serviceaftale;
    //This is apparently the simplest way to test if a date is within 30 days in pure javascript
    var date60DaysInFuture = new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000);
    this.partnerEndateSoon = data.serviceaftale.partner.validTo && new Date(data.serviceaftale.partner.validTo) < date60DaysInFuture;
    this.serviceordre = data.serviceordre;
    this.kreditvurdering = data.kreditvurdering;
  }

  ngOnInit(): void {
    const deadline = this.serviceordre
      ? new Date()
      : this.serviceaftale.naesteEftersynDate
      ? new Date(this.serviceaftale.naesteEftersynDate)
      : new Date();
    const note = this.serviceordre ? this.serviceordre.description : this.serviceaftale.naesteEftersynDescription;

    this.form = this.formBuilder.group({
      deadline: [deadline, Validators.required],
      note: [note, [Validators.required, Validators.maxLength(500)]],
      fastOrdreNote: [this.data.serviceaftale.fastOrdreNote, [Validators.maxLength(500)]],
      fakturaNote: [this.data.serviceordre?.fakturaNote, [Validators.maxLength(500)]],
      accept: [this.kreditvurdering?.kreditvaerdig == KreditvaerdigEnumDto.Yes],
      acceptTekst: [null, [Validators.maxLength(32)]],
    });

    this.loader.loadingStateChanged$.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    if (
      this.kreditvurdering?.kreditvaerdig == KreditvaerdigEnumDto.No ||
      this.kreditvurdering?.kreditvaerdig == KreditvaerdigEnumDto.Maybe ||
      this.kreditvurdering?.kreditvaerdig == KreditvaerdigEnumDto.NotSet
    ) {
      this.accept.setValidators([Validators.requiredTrue]);
      this.acceptTekst.setValidators([Validators.required, Validators.maxLength(32)]);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  async onSubmit() {
    if (await this.confirmCreateDialogService.Confirm(this.serviceaftale.serviceAftaleNo, OpgaveEnum.Eftersyn, this.deadline.value)) {
      if (this.serviceordre) {
        this.resendEftersyn();
      } else {
        this.createEftersyn();
      }
    }
  }

  resendEftersyn() {
    const request: ResendServiceordredRequestDto = {
      serviceordreNo: this.serviceordre.serviceordreNo,
      deadline: this.deadline.value.toOkDateString(),
      description: this.note.value,
      fastOrdreNote: this.fastOrdreNote.value,
      fakturaNote: this.fakturaNote.value,
      kreditadvarselAccept: this.accept.value
        ? {
            aarsagTekst: this.kreditvurdering.aarsagTekst,
            acceptTekst: this.acceptTekst.value,
            internalG4KreditkodeId: this.kreditvurdering.internalG4KreditkodeId,
            okKundeAftalenr: this.serviceaftale.okKundeAftaleNr,
            kreditvaerdig: this.kreditvurdering.kreditvaerdig,
          }
        : null,
    };

    this.loader.show();
    this.serviceordreService
      .resendServiceordre({ body: request, caller: EGIS_SERVICE_CALLER })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        () => {
          this.notification.success('Eftersyn genfremsendt.');
          this.dialogRef.close();
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  createEftersyn() {
    const request: CreateServiceordreRequestDto = {
      deadline: this.deadline.value.toOkDateString(),
      serviceaftaleNo: this.serviceaftale.serviceAftaleNo,
      serviceordreType: OpgaveEnum.Eftersyn,
      description: this.note.value,
      fastOrdreNote: this.fastOrdreNote.value,
      fakturaNote: this.fakturaNote.value,
      kreditvurderingAccept: this.accept.value
        ? {
            aarsagTekst: this.kreditvurdering.aarsagTekst,
            acceptTekst: this.acceptTekst.value,
            internalG4KreditkodeId: this.kreditvurdering.internalG4KreditkodeId,
            okKundeAftalenr: this.serviceaftale.okKundeAftaleNr,
            kreditvaerdig: this.kreditvurdering.kreditvaerdig,
          }
        : null,
    };

    this.loader.show();
    this.serviceordreService
      .createServiceordre({ body: request, caller: EGIS_SERVICE_CALLER })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (response: ServiceordreDto) => {
          this.notification.success('Eftersyn oprettet.');
          this.dialogRef.close(response);
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            this.validationErrors = [];

            for (var errorArray in error.error) {
              if (error.error.hasOwnProperty(errorArray)) {
                this.validationErrors = this.validationErrors.concat(error.error[errorArray]);
              }
            }
          } else {
            this.errorHandler.HandleError(error);
          }
        }
      );
  }
}
